import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Mail, ExternalLink, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface SignupConfirmationProps {
  email: string;
  confirmed?: boolean;
}

const SignupConfirmation: React.FC<SignupConfirmationProps> = ({ email, confirmed }) => {
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleResend = useCallback(async () => {
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) {
        toast({ title: "Couldn't resend", description: error.message });
      } else {
        toast({ title: "Email sent", description: "Check your inbox." });
        setResendCooldown(60);
      }
    } catch {
      toast({ title: "Error", description: "Please try again." });
    } finally {
      setResending(false);
    }
  }, [email]);

  if (confirmed) {
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10 ring-1 ring-green-500/20 animate-bounce">
          <CheckCircle2 className="h-7 w-7 text-green-500" />
        </div>
        <h3 className="text-lg font-medium text-foreground">Email Confirmed!</h3>
        <p className="text-sm text-muted-foreground">Taking you in...</p>
      </div>
    );
  }

  const domain = email.split('@')[1]?.toLowerCase();
  const isGmail = domain === 'gmail.com';
  const isOutlook = domain === 'outlook.com' || domain === 'hotmail.com' || domain === 'live.com';

  return (
    <div className="flex flex-col items-center gap-4 py-4 text-center">
      {/* Animated envelope */}
      <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/20">
        <Mail className="h-7 w-7 text-primary animate-pulse" />
      </div>

      <div className="space-y-1.5">
        <h3 className="text-lg font-medium text-foreground">Check Your Email</h3>
        <p className="text-sm text-muted-foreground max-w-[280px]">
          We sent a verification link to
        </p>
        <p className="text-sm font-medium text-foreground">{email}</p>
      </div>

      {/* Waiting indicator */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground/60">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Waiting for confirmation...</span>
      </div>

      {/* Quick links */}
      <div className="flex gap-2">
        {isGmail && (
          <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
            <a href="https://mail.google.com" target="_blank" rel="noopener noreferrer">
              Open Gmail <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          </Button>
        )}
        {isOutlook && (
          <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
            <a href="https://outlook.live.com" target="_blank" rel="noopener noreferrer">
              Open Outlook <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          </Button>
        )}
        {!isGmail && !isOutlook && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
              <a href="https://mail.google.com" target="_blank" rel="noopener noreferrer">Gmail</a>
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
              <a href="https://outlook.live.com" target="_blank" rel="noopener noreferrer">Outlook</a>
            </Button>
          </div>
        )}
      </div>

      {/* Resend */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleResend}
        disabled={resendCooldown > 0 || resending}
        className="text-xs text-muted-foreground"
      >
        {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend verification email'}
      </Button>

      <p className="text-[10px] text-muted-foreground/40">Check your spam folder too</p>
    </div>
  );
};

export default SignupConfirmation;
